import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "container" }
const _hoisted_2 = { class: "post__header" }
const _hoisted_3 = { class: "author" }
const _hoisted_4 = { class: "date" }
const _hoisted_5 = { class: "social" }
const _hoisted_6 = ["href"]
const _hoisted_7 = /*#__PURE__*/_createElementVNode("svg", { viewBox: "0 0 24 24" }, [
  /*#__PURE__*/_createElementVNode("path", { d: "M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" })
], -1)
const _hoisted_8 = [
  _hoisted_7
]
const _hoisted_9 = ["href"]
const _hoisted_10 = /*#__PURE__*/_createElementVNode("svg", { viewBox: "0 0 24 24" }, [
  /*#__PURE__*/_createElementVNode("path", { d: "M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z" })
], -1)
const _hoisted_11 = [
  _hoisted_10
]
const _hoisted_12 = ["innerHTML"]

import { computed, onMounted, reactive } from 'vue'
import { onBeforeRouteLeave, useRouter } from 'vue-router'
import { delay } from '@/utils/async'
import { renderToHtml } from '@/utils/markdown'
import { formatDate, urlFriendlyTitle } from '@/utils/text'
import { now } from '@/utils/time'
import { blogData, BlogPost } from '@/assets/data/blog'
import { useMeta } from 'vue-meta'

import Hero from '@/components/Hero.vue'

interface State {
  date: string
  isShowing: boolean
  post: BlogPost | null
  urlTitle: string
  content: string
}


export default /*#__PURE__*/_defineComponent({
  __name: 'BlogPost',
  setup(__props) {

const router = useRouter()

const state = reactive<State>({
  date: '',
  isShowing: false,
  post: null,
  urlTitle: '',
  content: ''
})

async function processContent () {
  if(!state.post) return
  const { default: content } = await import(`@/assets/blog/stories/${state.post.markdown}`)
  let el = new DOMParser().parseFromString(renderToHtml(content), 'text/html')
  let imgs = Array.from(el.getElementsByTagName('img'))
  imgs.forEach((img) => {
    if(img.src.includes('maxresdefault')) return
    const imgSrc = require(`@/assets/blog/${img.dataset.src}`)
    img.src = imgSrc
  })
  const body = el.body
  state.content = body.innerHTML
}

const facebookShareUrl = computed(() => {
  return `https://www.facebook.com/sharer.php?u=${document.location.href}`
})

const twitterShareUrl = computed(() => {
  return `https://twitter.com/intent/tweet?url=${document.location.href}&text=${encodeURIComponent(state.post?.title ?? 'Check out this ForeChamp blog post!')}`
})

onBeforeRouteLeave(async () => {
  state.isShowing = false
  await delay(800)
})

onMounted(async () => {
  state.date = router.currentRoute.value.params.id as string
  if(new Date(state.date) > now) {
    router.push('/fore-oh-fore')
    return
  }
  state.urlTitle = router.currentRoute.value.params.title as string
  const postDates = blogData.flatMap(x => x.date)
  const index = postDates.indexOf(state.date)
  if(index < 0) {
    router.push('/fore-oh-fore')
    return
  }
  state.post = blogData[index]
  useMeta({
    title: state.post.title,
    meta: [
      {
        property: 'og:title',
        content: state.post.title
      },
      {
        property: 'og:image',
        content: require(`@/assets/blog/hero/og/${state.post.image.replace('blog/hero/', '')}`)
      }
    ]
  })
  await processContent()
  await delay(200)
  state.isShowing = true
})

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (state.post)
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        class: _normalizeClass(["blog page-container transition__page", { 'isShowing': state.isShowing }])
      }, [
        _createVNode(Hero, {
          imageSrc: state.post.image,
          imageAlt: "Blog Post hero image"
        }, null, 8, ["imageSrc"]),
        _createElementVNode("section", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("h2", null, _toDisplayString(state.post.title), 1),
            _createElementVNode("p", null, [
              _createElementVNode("span", _hoisted_3, [
                _createTextVNode("By: "),
                _createVNode(_component_router_link, {
                  to: { name: 'BlogAuthor', params: { name: _unref(urlFriendlyTitle)(state.post.author) } }
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(state.post.author), 1)
                  ]),
                  _: 1
                }, 8, ["to"])
              ]),
              _createElementVNode("span", _hoisted_4, _toDisplayString(_unref(formatDate)(state.post.date)), 1),
              _createElementVNode("span", _hoisted_5, [
                _createElementVNode("a", {
                  href: _unref(facebookShareUrl),
                  class: "transition",
                  title: "Share this post to Facebook"
                }, _hoisted_8, 8, _hoisted_6),
                _createElementVNode("a", {
                  href: _unref(twitterShareUrl),
                  class: "transition",
                  target: "_blank",
                  title: "Share this post to Twitter"
                }, _hoisted_11, 8, _hoisted_9)
              ])
            ])
          ]),
          _createElementVNode("div", {
            class: "post__content",
            innerHTML: state.content
          }, null, 8, _hoisted_12)
        ])
      ], 2))
    : _createCommentVNode("", true)
}
}

})